import { Injectable } from '@angular/core';
import { BehaviorSubject  } from 'rxjs';
import { DescriptiveAlarm, WeatherStation, Reservoir, GlobalAlarms } from '../api/models';
import { EucsAlarmService, DashboardService, EucsDashboardService } from '../api/services'
import { ToastrService } from 'ngx-toastr';
import { ApplicationConfigService } from './application-config.service';

@Injectable({
  providedIn: 'root'
})
export class EucsAlarmDashboardService {
  constructor(
    private app: ApplicationConfigService,
    private alarmService: EucsAlarmService,
    private dashboardService: EucsDashboardService,
    private toastr: ToastrService
  ) { }

  private readonly _alarms = new BehaviorSubject<DescriptiveAlarm[]>([]);
  private readonly _dashboard = new BehaviorSubject<any>({
    meterDetails: { refreshing: false, loaded: false, data: [] },
    reservoirUsage: { refreshing: false, loaded: false, data: [] },
    scheduleRequirements: { refreshing: false, loaded: false, data: {} },
    customerUsages: { refreshing: false, loaded: false, data: [] },
    globalAlarms: { refreshing: false, loaded: false, data: { contaminatedWaterAlarm: false, highWindAlarm: false, highRainAlarm: false, awrfActive: false } },
    weatherStations: { refreshing: false, loaded: false, data: [] },
    evapotranspiration: {  DeviceInEto: 0, DeviceInPto: 0, DeviceInSmd: 0  }
  });

  readonly alarms$ = this._alarms.asObservable;
  readonly dashboard$ = this._dashboard.asObservable;

  public isDashboardRefreshing: boolean = this.dashboard.meterDetails.refreshing || this.dashboard.reservoirUsage.refreshing || this.dashboard.scheduleRequirements.refreshing || this.dashboard.customerUsages.refreshing;

  public isAlarmsRefreshing: boolean = false;
  public isAlarmsLoaded: boolean = false;

  get alarms(): DescriptiveAlarm[] {
    return this._alarms.getValue();
  }

  set alarms(val: DescriptiveAlarm[]) {
    this._alarms.next(val);
  }

  get dashboard() {
    return this._dashboard.getValue();
  }

  set dashboard(val: any) {
    this._dashboard.next(val);
  }

  removeAlarm(id: number) {
    this.alarms = this.alarms.filter(alarm => alarm.id !== id);
  }

  refreshData(getDashboard: boolean) {
    // Dashboard isn't always required to be called, ie. not on the home page and refreshing alarms. But alarms are always require to be refreshed.
    if (!this.isDashboardRefreshing && !this.isAlarmsRefreshing) {
      if (getDashboard) {
        this.dashboard.meterDetails.refreshing = true
        this.dashboard.reservoirUsage.refreshing = true
        this.dashboard.scheduleRequirements.refreshing = true
        this.dashboard.customerUsages.refreshing = true
        this.dashboard.globalAlarms.refreshing = true
        this.dashboard.weatherStations.refreshing = true  
      }

      this.isAlarmsRefreshing = true
      this.getAlarms(getDashboard)
    }
  }

  // Alarms
  getAlarms(getDashboard: boolean) {
    this.alarmService
      .EucsAlarmGetDescriptiveActiveAlarms(this.app.customerId)
      .subscribe(
        data => {
          this.isAlarmsRefreshing = false
          this.isAlarmsLoaded = true
          this.alarms = data

          if (getDashboard) {
            // dashboard called here as the alarms call is small and caches the LNM model that can then be used by the backend dashboard controller
            this.getDashboard();
          }
        }
      )
  }

  acknowledgeAlarm(id: number) {
    this.alarmService
      .EucsAlarmAcknowledge({customerId: this.app.customerId, id: id})
      .subscribe(
        data => {
          this.removeAlarm(id)
        },
        error => {
          this.toastr.error('Failed to Acknowledge Alarm', 'Acknowledge Alarm', { timeOut: 3000 });
        }
      )
  }

  // Dashboard Services 
  getDashboard() {
    this.getDashboardCommon();
    this.getDashboardMeterDetails();
    this.getDashboardScheduleRequirements();
    this.getDashboardCustomerUsages();
  }

  getDashboardCommon() {
    this.dashboardService
      .EucsDashboardGetDashboardCommon()
      .subscribe(
        data => {
          this.saveGlobalAlarms(data.alarms);
          this.saveDashboardWeatherStations(data.weatherStations);
          //this.saveDashboardReservoirUsages(data.Reservoirs)
        }
      )
  }
  
  getDashboardMeterDetails() {
    this.dashboardService
      .EucsDashboardGetMeterDetails()
      .subscribe(
        data => {
          this.dashboard.meterDetails.refreshing = false;
          this.dashboard.meterDetails.loaded = true;
          this.dashboard.meterDetails.data = data;
        }
      )
  } 
  
  getDashboardScheduleRequirements() {
    this.dashboardService
      .EucsDashboardGetScheduleRequirements()
      .subscribe(
        data => {
          this.dashboard.scheduleRequirements.refreshing = false;
          this.dashboard.scheduleRequirements.loaded = true;
          this.dashboard.scheduleRequirements.data = data;
        }
      )
  } 

  getDashboardCustomerUsages() {
    this.dashboardService
      .EucsDashboardGetCustomerUsages()
      .subscribe(
        data => {
          this.dashboard.customerUsages.refreshing = false;
          this.dashboard.customerUsages.loaded = true;
          this.dashboard.customerUsages.data = data;
        }
      )
  } 

  saveGlobalAlarms(data: GlobalAlarms) {
    this.dashboard.globalAlarms.refreshing = false;
    this.dashboard.globalAlarms.loaded = true;
    this.dashboard.globalAlarms.data.contaminatedWaterAlarm = data.contaminatedWaterAlarm;
    this.dashboard.globalAlarms.data.highRainAlarm = data.highRainAlarm;
    this.dashboard.globalAlarms.data.highWindAlarm = data.highWindAlarm;
    this.dashboard.globalAlarms.data.awrfActive = data.awrfActive;
  } 
  
  saveDashboardReservoirUsages(data: Reservoir[]) {
    this.dashboard.reservoirUsage.refreshing = false
    this.dashboard.reservoirUsage.loaded = true
    this.dashboard.reservoirUsage.data = data
  } 

  saveDashboardWeatherStations(data: WeatherStation[]) {
    this.dashboard.weatherStations.refreshing = false
    this.dashboard.weatherStations.loaded = true
    this.dashboard.weatherStations.data = data
    for (let weatherStation of data){
      if (weatherStation.supportsRain){
        this.dashboard.evapotranspiration = weatherStation;
        //console.log(this.dashboard.evapotranspiration);
      }
    }
  }
}
